/**
 * The developer docs as Markdown, for coding agents: every docs page at its own URL plus `.md`
 * (/developers/quickstart.md, the landing at /developers.md), and the same Markdown for a request to the page
 * itself that asks for `text/markdown` first. The proxy rewrites both, before the auth gate, to a static
 * route, /developers/md/<path> (app/developers/md), so every page's Markdown is public and cacheable, the
 * console's included, and none redirects to sign-in. A browser opening a `.md` URL gets the same text from
 * /developers/plain/<path>, as text/plain: a browser shows that, where it may offer to download Markdown.
 * Pure: the proxy, the page menu and the Markdown's own links share it.
 */

export const DEVELOPERS = "/developers";

/** Where the Markdown is served from: /developers/md for the landing, /developers/md/<path> for the rest. */
export const MARKDOWN_BASE = "/developers/md";

/** The same Markdown as plain text, for a browser that opens a `.md` URL. */
export const PLAIN_BASE = "/developers/plain";

/**
 * The Vary every answer of a docs page's URLs carries, its HTML and its Markdown alike: the router's headers
 * Next varies every app page by, then Accept. The proxy sets it whole, because the host keeps one Vary: on
 * staging a route's own `Vary: Accept` gave way to Next's list. Whichever the host keeps, or if it joins
 * them, Accept is in it.
 */
export const DOCS_VARY =
  "rsc, next-router-state-tree, next-router-prefetch, next-router-segment-prefetch, Accept";

/** The console's sections sit behind sign-in: only the console's own page has Markdown (its static words). */
const CONSOLE = "/developers/console";

/** A static page a rewrite serves (next.config.ts), not a docs page: it has no Markdown. */
const OPENAPI = "/developers/reference/openapi";

/**
 * The docs MCP (app/developers/docs-mcp): search_docs and read_doc over streamable HTTP, for anyone, with no
 * key. The proxy passes exactly this path on before the auth gate, as it rewrites a page's Markdown.
 */
export const DOCS_MCP = "/developers/docs-mcp";

/** The skills the site serves for coding agents (app/developers/skills), each a SKILL.md: files, not pages. */
export const SKILLS = "/developers/skills";

/**
 * The SDKs and the CLI the site serves to install from (public/developers/kit) until they are on npm and PyPI:
 * files, not pages. The proxy passes them on before the auth gate, as it does the docs MCP.
 */
export const KIT = "/developers/kit";

/**
 * Whether a path is a page of the developer site with Markdown of its own: /developers or a page under it,
 * never a file (search.json, v1.json, a picture, a skill, the kit), the Markdown route itself, the console's signed-in
 * sections, the OpenAPI explorer or the docs MCP. A path no page has (a typo) still counts: its Markdown
 * answers 404.
 */
export function hasMarkdown(path: string): boolean {
  if (path === DEVELOPERS) return true;
  if (!path.startsWith(`${DEVELOPERS}/`)) return false;
  for (const base of [MARKDOWN_BASE, PLAIN_BASE, SKILLS, KIT])
    if (path === base || path.startsWith(`${base}/`)) return false;
  if (path.startsWith(`${CONSOLE}/`) || path === OPENAPI || path === DOCS_MCP)
    return false;
  const last = path.slice(path.lastIndexOf("/") + 1);
  return last !== "" && !last.includes(".");
}

/** A docs page's Markdown: /developers/quickstart to /developers/quickstart.md, /developers to /developers.md. */
export function markdownHref(path: string): string {
  return `${path}.md`;
}

/** The origin this build serves, as the app's own canonical URLs say it (app/layout.tsx's metadataBase). */
export function siteOrigin(): string {
  // ||, not ??: a variable set but empty would make every URL relative.
  return (process.env.NEXT_PUBLIC_SITE_URL || "https://vestra.ai").replace(
    /\/+$/,
    ""
  );
}

/** The q a media type is given in an Accept header: 0 when it is not named. */
function quality(accept: string, type: string): number {
  for (const range of accept.split(",")) {
    const [name, ...params] = range.split(";").map((p) => p.trim());
    if (name?.toLowerCase() !== type) continue;
    const q = params.find((p) => /^q=/i.test(p));
    const n = q ? Number(q.slice(2)) : 1;
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/**
 * Whether a request asks for Markdown: it names `text/markdown`, at least as wanted as `text/html`. A browser
 * never names it, so a person always gets the page.
 */
export function wantsMarkdown(accept: string | null): boolean {
  if (!accept) return false;
  const markdown = quality(accept, "text/markdown");
  return markdown > 0 && markdown >= quality(accept, "text/html");
}

/** A browser's request: it names `text/html`, and does not ask for Markdown first. An agent's names neither. */
export function isBrowser(accept: string | null): boolean {
  return !!accept && !wantsMarkdown(accept) && quality(accept, "text/html") > 0;
}

/**
 * Where the proxy sends a request for a page's Markdown, or null when it is not one: a `.md` URL of a page
 * (as plain text for a browser, which then shows it), or the page itself asked for as `text/markdown`.
 */
export function markdownRewrite(
  pathname: string,
  accept: string | null
): string | null {
  const isMd = pathname.endsWith(".md");
  const asked = isMd
    ? pathname.slice(0, -".md".length)
    : wantsMarkdown(accept)
      ? pathname
      : null;
  if (asked === null || !hasMarkdown(asked)) return null;
  const base = isMd && isBrowser(accept) ? PLAIN_BASE : MARKDOWN_BASE;
  return `${base}${asked.slice(DEVELOPERS.length)}`;
}
